import { ControlCodes } from '@bcube/shared/constants';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { profileApi } from '@/api/admin/profiles';
import { userApi } from '@/api/admin/users';
import { AuditModal } from '@/components/modal/admin/user/audit-modal';
import { UserTable } from '@/components/table/admin/user-table';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { TableSkeleton } from '@/components/ui/skeleton';
import { useAuditModal } from '@/hooks/use-audit-modal';
import { usePermissions } from '@/hooks/use-permissions';

export function UserListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission } = usePermissions();
  const canCreate = hasPermission(ControlCodes.admin_user_create);
  const canEdit = hasPermission(ControlCodes.admin_user_update);
  const canViewHistory = hasPermission(ControlCodes.admin_user_view);

  // Audit modal hook
  const {
    isOpen: isAuditModalOpen,
    user: auditUser,
    currentPage: auditCurrentPage,
    auditData,
    isLoading: isAuditLoading,
    openModal: openAuditModal,
    closeModal: closeAuditModal,
    handlePageChange: handleAuditPageChange,
  } = useAuditModal();

  // Filter states
  const [filterName, setFilterName] = useState(searchParams.get('name') || '');
  const [filterCpf, setFilterCpf] = useState(searchParams.get('cpf') || '');
  const [filterEmail, setFilterEmail] = useState(searchParams.get('email') || '');
  const [filterUsername, setFilterUsername] = useState(searchParams.get('username') || '');
  const [filterProfile, setFilterProfile] = useState(searchParams.get('profileId') || 'all');
  const [filterStatus, setFilterStatus] = useState(searchParams.get('status') || 'all');

  const {
    data: profiles = [],
    isLoading: isProfilesLoading,
    isError: isProfilesError,
    refetch: refetchProfiles,
  } = useQuery({
    queryKey: ['profiles', 'active', 'user-filter'],
    queryFn: async () => {
      const first = await profileApi.findMany({ active: true, page: 1, limit: 100 });
      const profiles = [...first.docs];
      for (let page = 2; page <= first.pages; page++) {
        const result = await profileApi.findMany({ active: true, page, limit: 100 });
        profiles.push(...result.docs);
      }
      return profiles
        .filter((profile) => profile.active)
        .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
    },
  });

  const page = Number(searchParams.get('page')) || 1;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['users', Object.fromEntries(searchParams)],
    queryFn: () => userApi.findMany(Object.fromEntries(searchParams) as Record<string, string>),
  });

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', String(newPage));
    setSearchParams(params);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams();
    params.set('page', '1');

    if (filterName) params.set('name', filterName);
    if (filterCpf) params.set('cpf', filterCpf);
    if (filterEmail) params.set('email', filterEmail);
    if (filterUsername) params.set('username', filterUsername);
    if (filterProfile !== 'all') params.set('profileId', filterProfile);
    if (filterStatus !== 'all') params.set('status', filterStatus);

    if (params.toString() === searchParams.toString()) void refetch();
    setSearchParams(params);
  };

  const clearFilters = () => {
    setFilterName('');
    setFilterCpf('');
    setFilterEmail('');
    setFilterUsername('');
    setFilterProfile('all');
    setFilterStatus('all');
    setSearchParams(new URLSearchParams());
  };

  if (error) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-500">Erro ao carregar usuários</p>
        <Button onClick={() => window.location.reload()} className="mt-4">
          Tentar novamente
        </Button>
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Usuários</h1>
          <p className="mt-1 text-sm text-gray-500">
            Gerencie os usuários registrados na plataforma.
          </p>
        </div>
        {canCreate && (
          <Button asChild className="bg-black text-white hover:bg-gray-800">
            <Link to="/admin/users/create">
              <Plus className="h-4 w-4 mr-2" />
              Novo Usuário
            </Link>
          </Button>
        )}
      </div>

      {/* Search and Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filtros de busca</CardTitle>
          <CardDescription>
            Use os filtros e clique em Pesquisar para refinar a lista de usuários.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSearch} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <Input
                  label="Nome"
                  placeholder="Filtrar por nome"
                  value={filterName}
                  onChange={(e) => setFilterName(e.target.value)}
                />
              </div>
              <div>
                <Input
                  label="CPF"
                  placeholder="Filtrar por CPF"
                  value={filterCpf}
                  onChange={(e) => setFilterCpf(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="user-profile" className="mb-2 block text-sm font-medium">
                  Perfil
                </label>
                <Select
                  value={filterProfile}
                  onValueChange={setFilterProfile}
                  disabled={isProfilesLoading || isProfilesError}
                >
                  <SelectTrigger id="user-profile">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {profiles.map((profile) => (
                      <SelectItem key={profile.id} value={profile.id}>
                        {profile.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {isProfilesError && (
                  <p className="mt-1 text-sm text-destructive">
                    Erro ao carregar perfis.{' '}
                    <button type="button" className="underline" onClick={() => refetchProfiles()}>
                      Tentar novamente
                    </button>
                  </p>
                )}
              </div>
              <div>
                <Input
                  label="Usuário"
                  placeholder="Filtrar por usuário"
                  value={filterUsername}
                  onChange={(e) => setFilterUsername(e.target.value)}
                />
              </div>
              <div>
                <Input
                  label="Email"
                  placeholder="Filtrar por email"
                  value={filterEmail}
                  onChange={(e) => setFilterEmail(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="user-status" className="mb-2 block text-sm font-medium">
                  Status
                </label>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger id="user-status">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="active">Ativo</SelectItem>
                    <SelectItem value="blocked">Bloqueado</SelectItem>
                    <SelectItem value="inactive">Inativo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={clearFilters}>
                Limpar
              </Button>
              <Button type="submit" className="bg-black text-white hover:bg-gray-800">
                Pesquisar
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Table */}
      {isLoading ? (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <TableSkeleton rows={10} />
        </div>
      ) : data ? (
        <UserTable
          data={data}
          currentPage={page}
          onPageChange={handlePageChange}
          onOpenAuditModal={openAuditModal}
          canEdit={canEdit}
          canViewHistory={canViewHistory}
        />
      ) : null}

      {/* Audit Modal */}
      <AuditModal
        open={isAuditModalOpen}
        onClose={closeAuditModal}
        userName={auditUser?.name ?? ''}
        auditData={auditData}
        currentPage={auditCurrentPage}
        onPageChange={handleAuditPageChange}
        isLoading={isAuditLoading}
      />
    </motion.div>
  );
}
